import React, { useState, useEffect } from "react";
import { database } from "../../../firebaseConfig";
import { ref, set, push, update, remove, onValue, query } from "firebase/database";
import MicroserviceForm from "./MicroserviceForm";
import MicroserviceItem from "./MicroserviceItem";
import CsvHandler from "../utils/CsvHandler";
import "./MicroservicesCreator.css";

const MicroservicesCreator = ({ userId }) => {
  const [microservices, setMicroservices] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [editingMicroservice, setEditingMicroservice] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [categories, setCategories] = useState([]);
  const [sortBy, setSortBy] = useState("createdAt"); // createdAt, title, price
  const [sortDirection, setSortDirection] = useState("desc"); // asc, desc
  const [notification, setNotification] = useState({ message: "", type: "" });

  // Loading microservices from the database
  useEffect(() => {
    if (!userId) return;

    const microservicesRef = ref(database, `/World/${userId}/microservices`);
    
    const unsubscribe = onValue(microservicesRef, (snapshot) => {
      if (snapshot.exists()) {
        const microservicesData = snapshot.val();
        const microservicesArray = Object.entries(microservicesData).map(([id, data]) => ({
          id,
          ...data
        }));
        setMicroservices(microservicesArray);
        
        // Extract categories
        const uniqueCategories = [...new Set(microservicesArray
          .map(microservice => microservice.category)
          .filter(category => category && category.trim() !== "")
        )];
        setCategories(uniqueCategories);
      } else {
        setMicroservices([]);
        setCategories([]);
      }
      setIsLoading(false);
    });

    return () => unsubscribe();
  }, [userId]);

  // Save microservice (new or updated)
  const handleSaveMicroservice = (microserviceData) => {
    if (!userId) return;

    if (editingMicroservice) {
      // Update microservice
      const microserviceRef = ref(database, `/World/${userId}/microservices/${editingMicroservice.id}`);
      update(microserviceRef, microserviceData)
        .then(() => {
          showNotification("Microservice successfully updated", "success");
          setEditingMicroservice(null);
          setShowForm(false);
        })
        .catch(error => {
          console.error("Error updating microservice:", error);
          showNotification("Error updating microservice", "error");
        });
    } else {
      // Create new microservice
      const microservicesRef = ref(database, `/World/${userId}/microservices`);
      const newMicroserviceRef = push(microservicesRef);
      set(newMicroserviceRef, microserviceData)
        .then(() => {
          showNotification("Microservice successfully created", "success");
          setShowForm(false);
        })
        .catch(error => {
          console.error("Error creating microservice:", error);
          showNotification("Error creating microservice", "error");
        });
    }
  };

  // Prepare microservice for editing
  const handleEditMicroservice = (microservice) => {
    setEditingMicroservice(microservice);
    setShowForm(true);
  };

  // Delete microservice
  const handleDeleteMicroservice = (microserviceId) => {
    if (!userId) return;
    
    if (window.confirm("Do you really want to delete this microservice?")) {
      const microserviceRef = ref(database, `/World/${userId}/microservices/${microserviceId}`);
      remove(microserviceRef)
        .then(() => {
          showNotification("Microservice successfully deleted", "success");
        })
        .catch(error => {
          console.error("Error deleting microservice:", error);
          showNotification("Error deleting microservice", "error");
        });
    }
  };

  // Show notification and automatically hide it
  const showNotification = (message, type) => {
    setNotification({ message, type });
    
    // Automatically hide after 3 seconds
    setTimeout(() => {
      setNotification({ message: "", type: "" });
    }, 3000);
  };

  // CSV import success handler
  const handleCsvImportSuccess = (count) => {
    showNotification(`${count} microservices successfully imported from CSV`, "success");
  };

  // CSV import error handler
  const handleCsvImportError = (error) => {
    showNotification(`Error during CSV import: ${error.message}`, "error");
  };

  // Filter and sort microservices
  const getFilteredAndSortedMicroservices = () => {
    // Filter by search term and category
    const filtered = microservices.filter(microservice => {
      const matchesSearch = searchTerm.trim() === "" || 
        microservice.title.toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesCategory = categoryFilter === "" || microservice.category === categoryFilter;
      
      return matchesSearch && matchesCategory;
    });
    
    // Sort by the chosen criteria
    return filtered.sort((a, b) => {
      if (sortBy === "price") {
        return sortDirection === "asc" ? a.price - b.price : b.price - a.price;
      } else if (sortBy === "title") {
        return sortDirection === "asc" 
          ? a.title.localeCompare(b.title) 
          : b.title.localeCompare(a.title);
      } else { // createdAt
        return sortDirection === "asc" 
          ? a.createdAt - b.createdAt 
          : b.createdAt - a.createdAt;
      }
    });
  };

  const filteredAndSortedMicroservices = getFilteredAndSortedMicroservices();

  // Change sorting
  const handleSortChange = (field) => {
    if (sortBy === field) {
      // If already sorted by this field, reverse sort direction
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      // Otherwise sort by the new field (default descending)
      setSortBy(field);
      setSortDirection("desc");
    }
  };

  return (
    <div className="microservices-creator">
      {notification.message && (
        <div className={`microservices-creator__notification microservices-creator__notification--${notification.type}`}>
          {notification.message}
        </div>
      )}
      
      <div className="microservices-creator__header">
        <h2 className="microservices-creator__title">Manage Microservices</h2>
        {!showForm && (
          <button 
            className="microservices-creator__add-button" 
            onClick={() => {
              setEditingMicroservice(null);
              setShowForm(true);
            }}
          >
            <span className="microservices-creator__btn-icon">+</span> Create New Microservice
          </button>
        )}
      </div>
      
      {!showForm && (
        <div className="microservices-creator__csv-container">
          <div className="microservices-creator__csv-header">
            <span className="microservices-creator__csv-icon">📊</span>
            <h4>CSV Import/Export</h4>
          </div>
          <CsvHandler 
            userId={userId} 
            entityType="microservices" 
            onSuccess={handleCsvImportSuccess}
            onError={handleCsvImportError}
          />
          <div className="microservices-creator__csv-description">
            <strong>Note:</strong> With CSV import, you can import multiple microservices at once. 
            The CSV file should contain the columns <code>title</code>, <code>description</code>, <code>category</code>, and <code>price</code>. 
            The export downloads all current microservices as a CSV file.
          </div>
        </div>
      )}
      
      {showForm ? (
        <MicroserviceForm 
          onSubmit={handleSaveMicroservice} 
          initialMicroservice={editingMicroservice}
          isEditing={!!editingMicroservice}
          onCancel={() => {
            setEditingMicroservice(null);
            setShowForm(false);
          }}
          existingCategories={categories}
        />
      ) : (
        <>
          <div className="microservices-creator__filters">
            <div className="microservices-creator__search-container">
              <input 
                type="text" 
                placeholder="Search microservices..." 
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="microservices-creator__search-input"
              />
              {searchTerm && (
                <button 
                  className="microservices-creator__clear-search" 
                  onClick={() => setSearchTerm("")}
                >
                  ×
                </button>
              )}
            </div>
            
            <div className="microservices-creator__category-filter">
              <select 
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
                className="microservices-creator__category-select"
              >
                <option value="">All Categories</option>
                {categories.map(category => (
                  <option key={category} value={category}>
                    {category}
                  </option>
                ))}
              </select>
            </div>
            
            <div className="microservices-creator__sort-controls">
              <span className="microservices-creator__sort-label">Sort by:</span>
              <button 
                className={`microservices-creator__sort-button ${sortBy === "createdAt" ? "active" : ""}`}
                onClick={() => handleSortChange("createdAt")}
              >
                Date {sortBy === "createdAt" && (sortDirection === "asc" ? "↑" : "↓")}
              </button>
              <button 
                className={`microservices-creator__sort-button ${sortBy === "title" ? "active" : ""}`}
                onClick={() => handleSortChange("title")}
              >
                Name {sortBy === "title" && (sortDirection === "asc" ? "↑" : "↓")}
              </button>
              <button 
                className={`microservices-creator__sort-button ${sortBy === "price" ? "active" : ""}`}
                onClick={() => handleSortChange("price")}
              >
                Price {sortBy === "price" && (sortDirection === "asc" ? "↑" : "↓")}
              </button>
            </div>
          </div>
          
          <div className="microservices-creator__list">
            {isLoading ? (
              <div className="microservices-creator__loading">Loading microservices...</div>
            ) : filteredAndSortedMicroservices.length === 0 ? (
              <div className="microservices-creator__empty">
                {searchTerm || categoryFilter ? (
                  <p>No matching microservices found. <button className="microservices-creator__reset-filters" onClick={() => {
                    setSearchTerm("");
                    setCategoryFilter("");
                  }}>Reset filters</button></p>
                ) : (
                  <p>No microservices available yet. Create your first microservice!</p>
                )}
              </div>
            ) : (
              filteredAndSortedMicroservices.map(microservice => (
                <MicroserviceItem 
                  key={microservice.id} 
                  microservice={microservice} 
                  onEdit={handleEditMicroservice}
                  onDelete={handleDeleteMicroservice}
                />
              ))
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default MicroservicesCreator; 